/**
 * HelpGuide Component - Reusable help/tutorial component
 * 
 * Features:
 * - Collapsible help sections
 * - Step-by-step guides
 * - Inline tips
 * - Video/image support
 * - Accessible for beginners
 */
import React, { useState } from 'react';
import { 
  HelpCircle, 
  ChevronDown, 
  ChevronRight, 
  Lightbulb, 
  Info, 
  AlertCircle,
  BookOpen,
  Video,
  X
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from './button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from './collapsible';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './tooltip';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './popover';

/**
 * Inline Help Tooltip - Small icon with tooltip
 */
export function HelpTooltip({ children, className, side = "top" }) {
  return (
    <TooltipProvider>
      <Tooltip delayDuration={300}>
        <TooltipTrigger asChild>
          <button 
            type="button" 
            className={cn(
              "inline-flex items-center justify-center rounded-full",
              "text-muted-foreground hover:text-primary transition-colors",
              "focus:outline-none focus:ring-2 focus:ring-primary/20",
              className
            )}
          >
            <HelpCircle className="h-4 w-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent side={side} className="max-w-xs text-sm">
          {children}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * Help Popover - Larger help content with popover
 */
export function HelpPopover({ 
  trigger, 
  title, 
  children, 
  className,
  side = "right",
  align = "start"
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        {trigger || (
          <Button variant="ghost" size="icon" className={cn("h-8 w-8", className)}>
            <HelpCircle className="h-4 w-4" />
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent side={side} align={align} className="w-80">
        {title && (
          <div className="flex items-center gap-2 mb-3 pb-2 border-b">
            <Lightbulb className="h-4 w-4 text-yellow-500" />
            <h4 className="font-medium">{title}</h4>
          </div>
        )}
        <div className="text-sm text-muted-foreground">
          {children}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Help Alert - Inline help box
 */
export function HelpAlert({ 
  variant = "info", 
  title, 
  children, 
  className,
  dismissible = false,
  onDismiss
}) {
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;

  const variants = {
    info: {
      bg: "bg-blue-500/10 border-blue-500/30",
      icon: <Info className="h-4 w-4 text-blue-500 flex-shrink-0" />,
      titleColor: "text-blue-600 dark:text-blue-400"
    },
    tip: {
      bg: "bg-yellow-500/10 border-yellow-500/30",
      icon: <Lightbulb className="h-4 w-4 text-yellow-500 flex-shrink-0" />,
      titleColor: "text-yellow-600 dark:text-yellow-400"
    },
    warning: {
      bg: "bg-orange-500/10 border-orange-500/30",
      icon: <AlertCircle className="h-4 w-4 text-orange-500 flex-shrink-0" />,
      titleColor: "text-orange-600 dark:text-orange-400"
    },
    guide: {
      bg: "bg-purple-500/10 border-purple-500/30",
      icon: <BookOpen className="h-4 w-4 text-purple-500 flex-shrink-0" />,
      titleColor: "text-purple-600 dark:text-purple-400"
    }
  };

  const v = variants[variant] || variants.info;

  const handleDismiss = () => {
    setDismissed(true);
    onDismiss?.();
  };

  return (
    <div 
      className={cn(
        "p-3 rounded-lg border",
        v.bg,
        className
      )}
      data-testid="help-alert"
    >
      <div className="flex items-start gap-3">
        {v.icon}
        <div className="flex-1 min-w-0">
          {title && (
            <h4 className={cn("font-medium text-sm mb-1", v.titleColor)}>
              {title}
            </h4>
          )}
          <div className="text-sm text-muted-foreground">
            {children}
          </div>
        </div>
        {dismissible && (
          <button 
            onClick={handleDismiss}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Help Section - Collapsible section with help content
 */
export function HelpSection({ 
  title, 
  children, 
  defaultOpen = false,
  className,
  icon
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <Collapsible 
      open={isOpen} 
      onOpenChange={setIsOpen}
      className={cn("border rounded-lg", className)}
    >
      <CollapsibleTrigger asChild>
        <button 
          className={cn(
            "flex items-center justify-between w-full p-3",
            "text-left hover:bg-muted/50 transition-colors",
            "rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
          )}
        >
          <div className="flex items-center gap-2">
            {icon || <HelpCircle className="h-4 w-4 text-primary" />}
            <span className="font-medium text-sm">{title}</span>
          </div>
          {isOpen ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          )}
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="px-3 pb-3 text-sm text-muted-foreground">
          {children}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

/**
 * Help Steps - Step-by-step guide
 */
export function HelpSteps({ steps, className }) {
  return (
    <div className={cn("space-y-3", className)}>
      {steps.map((step, index) => (
        <div key={index} className="flex gap-3">
          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-medium flex items-center justify-center">
            {index + 1}
          </div>
          <div className="flex-1 pt-0.5">
            {step.title && (
              <h5 className="font-medium text-sm mb-1">{step.title}</h5>
            )}
            <p className="text-sm text-muted-foreground">{step.description}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Full Help Guide Panel - Complete guide component
 */
export function HelpGuide({ 
  title,
  description,
  sections = [],
  tips = [],
  videoUrl,
  className,
  collapsible = true,
  defaultOpen = true
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const content = (
    <div className="space-y-4">
      {description && (
        <p className="text-sm text-muted-foreground">{description}</p>
      )}

      {/* Video Link */}
      {videoUrl && (
        <a 
          href={videoUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={cn(
            "flex items-center gap-2 p-3 rounded-lg",
            "bg-red-500/10 border border-red-500/30",
            "text-sm text-red-600 dark:text-red-400 hover:bg-red-500/20 transition-colors"
          )}
        >
          <Video className="h-4 w-4" />
          <span>Assistir tutorial em vídeo</span>
        </a>
      )}

      {/* Sections */}
      {sections.length > 0 && (
        <div className="space-y-2">
          {sections.map((section, index) => (
            <HelpSection 
              key={index} 
              title={section.title}
              icon={section.icon}
              defaultOpen={section.defaultOpen}
            >
              {section.content}
              {section.steps && <HelpSteps steps={section.steps} className="mt-3" />}
            </HelpSection>
          ))}
        </div>
      )}

      {/* Quick Tips */}
      {tips.length > 0 && (
        <div className="space-y-2">
          <h5 className="text-sm font-medium flex items-center gap-2">
            <Lightbulb className="h-4 w-4 text-yellow-500" />
            Dicas rápidas
          </h5>
          <ul className="text-sm text-muted-foreground space-y-1 pl-6">
            {tips.map((tip, index) => (
              <li key={index} className="list-disc">{tip}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );

  if (!collapsible) {
    return (
      <div className={cn("p-4 border rounded-lg bg-muted/30", className)} data-testid="help-guide">
        {title && (
          <div className="flex items-center gap-2 mb-4 pb-3 border-b">
            <BookOpen className="h-5 w-5 text-primary" />
            <h3 className="font-semibold">{title}</h3>
          </div>
        )}
        {content}
      </div>
    );
  }

  return (
    <Collapsible 
      open={isOpen} 
      onOpenChange={setIsOpen}
      className={cn("border rounded-lg bg-muted/30", className)}
      data-testid="help-guide"
    >
      <CollapsibleTrigger asChild>
        <button 
          className={cn(
            "flex items-center justify-between w-full p-4",
            "text-left hover:bg-muted/50 transition-colors rounded-lg"
          )}
        >
          <div className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-primary" />
            <span className="font-semibold">{title || "Guia de Ajuda"}</span>
          </div>
          {isOpen ? (
            <ChevronDown className="h-5 w-5 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-5 w-5 text-muted-foreground" />
          )}
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="px-4 pb-4">
          {content}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

export default HelpGuide;
